import PropTypes from "prop-types";
import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Layout from "../../layouts/Layout";
import ProductImageDescription from "../../wrappers/product/ProductImageDescription";
import { useTranslation } from "react-i18next";
import { Link, Redirect, useParams } from "react-router-dom";
import urls, { categorySlug, productsSlug } from "../../urls";
import { WEBSITE_NAME } from "../../constants/CompanyConstants";
import { META_PRODUCT_DESCRIPTION } from "../../constants/MetaTagsConstants";
import ProductInformationTab from "../../wrappers/product/ProductInformationTab";
import RelatedProductSlider from "../../components/product/RelatedProducts";
import CategoryFull from "../../components/category/CategoryFull";
import {
  formatCategoryName,
  formatProductMetaTitleSlogan,
} from "../../helpers/formatters";
import { Helmet } from "react-helmet";
import { ProductStructuredData } from "../../components/structured-data/ProductStructuredData";
import { BreadcrumbStructuredData } from "../../components/structured-data/BreadcrumbStructuredData";
import {
  BREADCRUMB_HOME,
  BREADCRUMB_PRODUCTS,
} from "../../constants/BreadcrumbConstants";
import { LIST_TYPE_FEATURED } from "../../constants/FeaturedProductsConstants";
import {
  findCategoryById,
  findMainCategoryBySubId,
} from "../../helpers/categories";
import { concat } from "lodash";
import { StyledBreadcrumbs } from "../../styles/SharedStyles";
import stepsBannerData from "../../data/banner/steps-banner.json";
import StepsBanner from "../../components/banner/StepsBanner";
import StoryBanner from "../../components/banner/StoryBanner";
import pageData from "../../data/product-detail/product-detail-data.json";
import useIsMobile from "../../components/hooks/useIsMobile";
import { trackProductViewed } from "../../helpers/posthogAnalytics";

const Product = () => {
  const { url } = useParams();
  const { t, i18n } = useTranslation();
  const isMobile = useIsMobile();
  // Get product by id out of redux state
  const product = useSelector((state) =>
    state.firestoreReducer.ordered.products.find((data) => data.url === url),
  );
  const MAX_RELATED_PRODUCTS = 8;
  const products = useSelector(
    (state) => state.firestoreReducer.ordered.products,
  );
  const categories = useSelector(
    (state) => state.firestoreReducer.ordered.categories,
  );
  const featuredProductsLists = useSelector(
    (state) => state.firestoreReducer.ordered.featuredProducts,
  );
  const featuredList = featuredProductsLists.find(
    (item) => item.type === LIST_TYPE_FEATURED,
  );
  const [isFeatured, setIsFeatured] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [breadcrumbCategories, setBreadcrumbCategories] = useState([]);
  const DATA = pageData[i18n.language];

  useEffect(() => {
    if (
      !!product &&
      featuredList?.products?.length > 0 &&
      featuredList?.products?.includes(product.id)
    )
      setIsFeatured(true);
    else setIsFeatured(false);

    // Calculate related products
    if (!product || !products || !categories) return;
    const related = products.filter((p) => {
      let category = findCategoryById(categories, product.categories[0]);
      if (!!category && category.subcategoryId) {
        return (
          p.categories.includes(category.subcategoryId) && p.id !== product.id
        );
      } else {
        return (
          p.categories.includes(category?.categoryId) && p.id !== product.id
        );
      }
    });
    setRelatedProducts(related.slice(0, MAX_RELATED_PRODUCTS));

    // Set product category
  }, [product, featuredList, categories]);

  useEffect(() => {
    if (!!product && !!categories) {
      // Set category
      const category = findCategoryById(categories, product.categories[0]);

      if (!!category) {
        setCategory(category);

        // Set breadcrumbs
        if (category.subcategory) {
          const mainCategory = findMainCategoryBySubId(
            categories,
            product.categories[0],
          );
          setBreadcrumbCategories([
            {
              name: formatCategoryName(mainCategory, i18n.language),
              url: urls.categorySlug + mainCategory.slug,
            },
            {
              name: formatCategoryName(category, i18n.language),
              url: urls.categorySlug + mainCategory.slug + "/" + category.slug,
            },
          ]);
        } else {
          setBreadcrumbCategories([
            {
              name: formatCategoryName(category, i18n.language),
              url: urls.categorySlug + category.slug,
            },
          ]);
        }
      }
    }
  }, [product, categories]);

  useEffect(() => {
    if (product) {
      trackProductViewed(product);
    }
  }, [product]);

  if (!product) return <Redirect to={urls.not_found} />;

  return (
    <Fragment>
      <Helmet>
        <title>
          {!!product ? product.name : t("products")} ·{" "}
          {formatProductMetaTitleSlogan(category?.name)} · {WEBSITE_NAME}
        </title>
        <meta
          name="description"
          content={"Bestel " + product.name + " " + META_PRODUCT_DESCRIPTION}
        />
        <meta
          property="og:title"
          content={product.name + " · " + WEBSITE_NAME}
        />
        <meta
          name="og:description"
          content={"Bestel " + product.name + " " + META_PRODUCT_DESCRIPTION}
        />
        <meta
          property="og:url"
          content={
            process.env.REACT_APP_WEBSITE_URL + urls.product + product.url
          }
        />
        <meta property="og:image" content={product.image[0]} />
        {ProductStructuredData(product)}
        {BreadcrumbStructuredData([
          BREADCRUMB_HOME,
          BREADCRUMB_PRODUCTS,
          ...breadcrumbCategories.map((category) => ({
            name: category.name,
            url:
              process.env.REACT_APP_WEBSITE_URL +
              category.url.replace(productsSlug, categorySlug),
          })),
          {
            name: product.name,
            url: process.env.REACT_APP_WEBSITE_URL + urls.product + product.url,
          },
        ])}
      </Helmet>
      <Layout headerTop="visible">
        <StyledBreadcrumbs
          items={concat(
            [
              {
                name: t("products"),
                url: urls.shop,
              },
            ],
            !!breadcrumbCategories && breadcrumbCategories.length > 0
              ? breadcrumbCategories
              : [],
          ).flatMap((item) => {
            if (!!item) {
              return {
                title: <Link to={item.url}>{item.name}</Link>,
              };
            } else return null;
          })}
          style={{ marginBottom: "1rem" }}
        />

        {/* product description with image */}
        <ProductImageDescription
          product={!!product ? product : {}}
          isFeatured={isFeatured}
        />

        {/*/!* product description tab *!/*/}
        {isMobile &&
          (!!product.additionalInfo ||
            !!product.description ||
            !!product.allergens) && (
            <ProductInformationTab
              additionalInfoTitle={t("additional_info")}
              additionalInfo={product.additionalInfo}
              description={product.description}
              allergens={product.allergens}
            />
          )}
        {!!relatedProducts && relatedProducts.length > 0 && (
          <RelatedProductSlider products={relatedProducts} />
        )}
        <StoryBanner data={DATA?.story1} dark={false} />
        <StepsBanner data={stepsBannerData} />
        <CategoryFull category={category?.slug} />
      </Layout>
    </Fragment>
  );
};

Product.propTypes = {
  location: PropTypes.object,
};

export default Product;
